import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Mercadão PDP — galeria, preços, variantes (ES5-friendly, sem class/#).
 * @param {HTMLElement} root
 */
function MercadaoProductPage(root) {
  this.root = root;
  this.moneyFormat = root.dataset.moneyFormat || window.Theme?.moneyFormat || '{{amount}}';
  this.currency = root.dataset.currency || window.Theme?.currency || 'BRL';
  this.pixDiscount = Number(root.dataset.pixDiscount || 5) / 100;
  this.installments = Number(root.dataset.installments || 12);
  this.cashbackPercent = Number(root.dataset.cashbackPercent || 0) / 100;
  this.freeShippingMin = Number(root.dataset.freeShippingMin || 29900);
  this.viewersMin = Number(root.dataset.viewersMin || 18);
  this.viewersMax = Number(root.dataset.viewersMax || 98);
  this.tplFreeShipping = root.dataset.templateFreeShipping || 'Frete grátis';
  this.tplFreeShippingFrom =
    root.dataset.templateFreeShippingFrom || 'Frete grátis a partir de __AMOUNT__';
  this.promoEnd = root.dataset.promoEnd || '';
  this.tplViewers = root.dataset.templateViewers || '__COUNT__ vendo agora';
  this.galleryMap = this.parseGalleryMap();

  this.root._mercadaoPdp = this;

  this.bindGalleryZoom();
  this.bindVariantPickerGallery();
  this.bindBuyNow();
  this.bindAddCartBusy();
  this.bindNavScroll();
  this.bindVariantUpdates();
  this.bindFormVariantSync();

  const initialPrice = Number(root.dataset.variantPrice || 0);
  const initialCompare = Number(root.dataset.variantCompare || 0);
  if (initialPrice > 0) {
    this.updatePrices({
      price: initialPrice,
      compare_at_price: initialCompare > initialPrice ? initialCompare : null,
    });
  } else {
    this.updateShippingTag(initialPrice);
  }

}

MercadaoProductPage.prototype.parseGalleryMap = function () {
  const el = this.root.querySelector('[data-md-gallery-variant-map]');
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[Mercadão PDP] mapa da galeria inválido', e);
    return null;
  }
};

MercadaoProductPage.prototype.getSelectedVariantId = function () {
  const form = this.root.querySelector('form[action*="/cart/add"]');
  if (!form) return null;
  const input = form.querySelector('[name="id"]');
  return input && input.value ? String(input.value) : null;
};

MercadaoProductPage.prototype.getMaxGallerySlots = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const map = this.galleryMap;
  return (map && map.maxSlots) || Number((grid && grid.dataset.slots) || 4) || 4;
};

MercadaoProductPage.prototype.resolveVisualGroupValue = function () {
  const map = this.galleryMap;
  if (!map) return null;

  const picker = this.root.querySelector('variant-picker.mercadao-pdp-variant-picker');
  if (picker) {
    const visualFieldset = picker.querySelector('[data-md-visual-option="true"]');
    if (visualFieldset) {
      const visualChecked = visualFieldset.querySelector('input[type="radio"]:checked');
      if (visualChecked) return visualChecked.value?.trim() || null;
    }
  }

  const visualKey = map.visualOption || 'option1';
  const pos = visualKey === 'option2' ? 2 : visualKey === 'option3' ? 3 : 1;
  if (!picker) return null;

  for (const fieldset of picker.querySelectorAll('fieldset')) {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!checked) continue;
    const inputPos = Number(checked.dataset.inputId?.split('-')[0] || 0);
    if (inputPos === pos) {
      const value = checked.value?.trim();
      return value || null;
    }
  }

  if (map.resolver?.groupsOrdered?.length === 1) {
    return map.resolver.groupsOrdered[0];
  }
  return null;
};

MercadaoProductPage.prototype.getVariantImageIds = function (variant) {
  const map = this.galleryMap;
  if (!map) return [];

  let groupValue = this.resolveVisualGroupValue();

  if (!groupValue && variant && window.MercadaoPdpGallery) {
    groupValue = window.MercadaoPdpGallery.groupForVariant(
      variant,
      map.visualOption || 'option1'
    );
  }

  const visualKey = map.visualOption || 'option1';
  if (!groupValue && variant) {
    const entry =
      variant.id != null && map.variants ? map.variants[String(variant.id)] : null;
    groupValue =
      entry?.groupValue != null
        ? entry.groupValue
        : variant[visualKey] != null
          ? variant[visualKey]
          : null;
  }

  if (
    groupValue &&
    map.byVisual &&
    map.byVisual[groupValue] &&
    map.byVisual[groupValue].imageIds &&
    map.byVisual[groupValue].imageIds.length
  ) {
    return this.orderGalleryImageIds(
      map.byVisual[groupValue].imageIds.map(String),
      groupValue,
      variant
    );
  }

  if (window.MercadaoPdpGallery && map.resolver && groupValue) {
    const computed = window.MercadaoPdpGallery.computeForGroup(map, groupValue);
    if (computed?.length) return this.orderGalleryImageIds(computed, groupValue, variant);
  }

  const variantId =
    variant && variant.id != null
      ? String(variant.id)
      : this.getSelectedVariantId();

  if (!variantId || !map.variants || !map.variants[variantId]) return [];

  const entry = map.variants[variantId];
  const imageIds = entry.imageIds || entry.mediaIds || [];
  if (!imageIds || !imageIds.length) return [];
  return this.orderGalleryImageIds(imageIds.map(String), groupValue, variant);
};

MercadaoProductPage.prototype.orderGalleryImageIds = function (imageIds, groupValue, variant) {
  const map = this.galleryMap;
  if (
    !imageIds?.length ||
    !groupValue ||
    !window.MercadaoPdpGallery?.orderGroupImageIds ||
    !map?.resolver?.images
  ) {
    return imageIds;
  }

  const include = {};
  imageIds.forEach((id) => {
    include[String(id)] = true;
  });

  return (
    window.MercadaoPdpGallery.orderGroupImageIds(
      map.resolver.images,
      include,
      groupValue,
      map.allVariants || [],
      map.visualOption || 'option1',
      this.getFeaturedImageId(variant)
    ) || imageIds
  );
};

MercadaoProductPage.prototype.getGridImageIdsFromList = function (imageIds) {
  if (!imageIds || !imageIds.length) return [];
  const maxSlots = this.getMaxGallerySlots();
  return imageIds.slice(0, maxSlots);
};

MercadaoProductPage.prototype.getGalleryImageIds = function (variant) {
  const v =
    variant ||
    this.getPickerVariant() ||
    (this.getSelectedVariantId() ? { id: this.getSelectedVariantId() } : null);
  return this.getVariantImageIds(v);
};

MercadaoProductPage.prototype.getFeaturedImageId = function (variant) {
  if (!variant) return null;
  if (variant.featured_media && variant.featured_media.id != null) {
    return String(variant.featured_media.id);
  }
  if (variant.featured_image && variant.featured_image.id != null) {
    return String(variant.featured_image.id);
  }
  return null;
};

MercadaoProductPage.prototype.ensureGalleryLightbox = function () {
  if (this._galleryLightbox) return this._galleryLightbox;

  const dialog = this.root.querySelector('[data-md-gallery-lightbox]');
  if (!(dialog instanceof HTMLDialogElement)) return null;

  const track = dialog.querySelector('[data-md-lightbox-track]');
  const counter = dialog.querySelector('[data-md-lightbox-counter]');
  const prev = dialog.querySelector('[data-md-lightbox-prev]');
  const next = dialog.querySelector('[data-md-lightbox-next]');

  const close = () => {
    if (dialog.open) dialog.close();
    document.body.classList.remove('md-pdp-lightbox-open');
  };

  dialog.addEventListener('close', () => {
    document.body.classList.remove('md-pdp-lightbox-open');
  });

  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });

  dialog.querySelectorAll('[data-md-lightbox-close]').forEach((btn) => {
    btn.addEventListener('click', close);
  });

  const go = (delta) => {
    if (!this._lightboxSlideCount) return;
    this._lightboxIndex =
      (this._lightboxIndex + delta + this._lightboxSlideCount) % this._lightboxSlideCount;
    this.updateLightboxSlide();
  };

  if (prev) prev.addEventListener('click', () => go(-1));
  if (next) next.addEventListener('click', () => go(1));

  dialog.addEventListener('keydown', (e) => {
    if (!dialog.open) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    }
  });

  const viewport = dialog.querySelector('.md-pdp-lightbox__viewport');
  if (viewport) {
    let touchStartX = 0;
    viewport.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0]?.clientX ?? 0;
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchend',
      (e) => {
        const touch = e.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - touchStartX;
        if (Math.abs(dx) < 40) return;
        go(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  this._galleryLightbox = { dialog, track, counter, prev, next, close };
  return this._galleryLightbox;
};

MercadaoProductPage.prototype.buildLightboxSlides = function (imageIds) {
  const map = this.galleryMap;
  const lb = this.ensureGalleryLightbox();
  if (!lb || !map || !map.images || !lb.track) return false;

  const slides = [];
  imageIds.forEach((id) => {
    const data = map.images[String(id)];
    if (data && data.src) {
      slides.push({ id: String(id), src: data.src, alt: data.alt || '' });
    }
  });

  if (!slides.length) return false;

  lb.track.innerHTML = '';
  slides.forEach((slide, index) => {
    const li = document.createElement('li');
    li.className = 'md-pdp-lightbox__slide';
    li.setAttribute('role', 'listitem');
    li.setAttribute('data-md-lightbox-slide', '');
    li.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    const img = document.createElement('img');
    img.src = slide.src;
    img.alt = slide.alt;
    img.loading = index === 0 ? 'eager' : 'lazy';
    li.appendChild(img);
    lb.track.appendChild(li);
  });

  this._lightboxSlideCount = slides.length;
  return true;
};

MercadaoProductPage.prototype.updateLightboxSlide = function () {
  const lb = this._galleryLightbox;
  if (!lb || !lb.track) return;

  const index = this._lightboxIndex || 0;
  const total = this._lightboxSlideCount || 0;
  lb.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;

  lb.track.querySelectorAll('[data-md-lightbox-slide]').forEach((slide, i) => {
    slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
  });

  if (lb.counter) {
    const tpl = this.root.dataset.galleryCounterTemplate || '__CURRENT__ de __TOTAL__';
    lb.counter.textContent = tpl
      .replace('__CURRENT__', String(index + 1))
      .replace('__TOTAL__', String(total));
  }

  if (lb.prev) lb.prev.disabled = total <= 1;
  if (lb.next) lb.next.disabled = total <= 1;
};

MercadaoProductPage.prototype.openGalleryLightbox = function (startImageId) {
  const imageIds = this.getGalleryImageIds();
  if (!imageIds.length) return;

  if (!this.buildLightboxSlides(imageIds)) return;

  let startIndex = 0;
  if (startImageId != null) {
    const idx = imageIds.indexOf(String(startImageId));
    if (idx >= 0) startIndex = idx;
  }

  this._lightboxIndex = startIndex;
  this.updateLightboxSlide();

  const lb = this._galleryLightbox;
  if (lb && lb.dialog && !lb.dialog.open) {
    lb.dialog.showModal();
    document.body.classList.add('md-pdp-lightbox-open');
  }
};

MercadaoProductPage.prototype.bindGalleryZoom = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  if (!grid) return;
  grid.addEventListener('click', (e) => {
    const cell = e.target.closest('[data-md-gallery-cell]');
    if (!cell) return;
    e.preventDefault();
    const imageId = cell.getAttribute('data-image-id');
    this.openGalleryLightbox(imageId);
  });
};

MercadaoProductPage.prototype.renderGalleryWithImageIds = function (gridIds, fullImageIds) {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const map = this.galleryMap;
  const imageIds = gridIds || fullImageIds;
  if (!grid || !map || !map.images || !imageIds || !imageIds.length) return;

  const maxSlots = this.getMaxGallerySlots();
  const totalImages = (fullImageIds && fullImageIds.length) || imageIds.length;
  const moreCount = Math.max(0, totalImages - maxSlots);
  const zoomLabel = this.root.dataset.galleryZoomLabel || 'Ampliar foto __INDEX__';
  const moreBadgeTpl =
    this.root.dataset.galleryMoreBadge || '+__COUNT__';

  grid.dataset.totalImages = String(totalImages);
  grid.innerHTML = '';

  for (let slot = 0; slot < maxSlots; slot += 1) {
    const id = imageIds[slot];
    const data = id != null ? map.images[String(id)] : null;

    if (data && data.src900) {
      const btn = document.createElement('button');
      btn.type = 'button';
      const isLastWithMore = slot === maxSlots - 1 && moreCount > 0;
      btn.className = 'md-pdp__gallery-cell' + (isLastWithMore ? ' md-pdp__gallery-cell--has-more' : '');
      btn.setAttribute('role', 'listitem');
      btn.setAttribute('data-md-gallery-cell', '');
      btn.setAttribute('data-image-id', String(id));
      btn.setAttribute('aria-label', zoomLabel.replace('__INDEX__', String(slot + 1)));
      const img = document.createElement('img');
      img.className = 'md-pdp__gallery-cell-img';
      img.alt = data.alt || '';
      img.loading = slot === 0 ? 'eager' : 'lazy';
      img.removeAttribute('src');
      img.src = data.src900;
      img.sizes = '(min-width: 990px) 26vw, 46vw';
      btn.appendChild(img);
      if (isLastWithMore) {
        const badge = document.createElement('span');
        badge.className = 'md-pdp__gallery-cell-more';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = moreBadgeTpl.replace('__COUNT__', String(moreCount));
        btn.appendChild(badge);
      }
      grid.appendChild(btn);
    } else {
      const empty = document.createElement('div');
      empty.className = 'md-pdp__gallery-cell md-pdp__gallery-cell--empty';
      empty.setAttribute('role', 'listitem');
      empty.setAttribute('aria-hidden', 'true');
      grid.appendChild(empty);
    }
  }
};

MercadaoProductPage.prototype.renderGalleryFromFeaturedMedia = function (variant) {
  const variantIds = this.getVariantImageIds(variant);
  if (variantIds.length) {
    const gridIds = this.getGridImageIdsFromList(variantIds);
    this.renderGalleryWithImageIds(gridIds, variantIds);
    return true;
  }

  return false;
};

MercadaoProductPage.prototype.updateGalleryFromVariant = function (variant) {
  if (!variant || !variant.id) return;

  if (this._galleryLightbox && this._galleryLightbox.dialog && this._galleryLightbox.dialog.open) {
    this._galleryLightbox.close();
  }

  const variantIds = this.getVariantImageIds(variant);
  if (variantIds.length) {
    const gridIds = this.getGridImageIdsFromList(variantIds);
    this.renderGalleryWithImageIds(gridIds, variantIds);
  }
};

MercadaoProductPage.prototype.getPickerVariant = function () {
  const script = this.root.querySelector(
    'variant-picker.mercadao-pdp-variant-picker script[type="application/json"]'
  );
  if (!script || !script.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
};

MercadaoProductPage.prototype.bindFormVariantSync = function () {
  const form = this.root.querySelector('[data-md-product-form]');
  if (!form) return;

  form.addEventListener('submit', () => {
    const input = form.querySelector('[name="id"]');
    if (!input) return;
    const radios = this.root.querySelectorAll(
      'variant-picker.mercadao-pdp-variant-picker fieldset input[type="radio"]:checked'
    );
    if (radios.length < 2) {
      const variant = this.getPickerVariant();
      if (variant?.id) input.value = String(variant.id);
    }
  });
};

MercadaoProductPage.prototype.bindVariantPickerGallery = function () {
  /* Galeria na troca de variante: mercadao-pdp-sticky-sync.js + variant:update */
};

MercadaoProductPage.prototype.setCommerceBusy = function (busy) {
  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const addBtn = this.root.querySelector('[data-md-add-cart]');
  const stickyBtn = document.querySelector('[data-md-sticky-add]');

  [buyNow, addBtn, stickyBtn].forEach((el) => {
    if (!el) return;
    el.classList.toggle('md-pdp__btn--busy', busy);
    if (busy) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  });
};

MercadaoProductPage.prototype.resetCommerceState = function () {
  this.setCommerceBusy(false);

  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const addBtn = this.root.querySelector('[data-md-add-cart]');
  const stickyBtn = document.querySelector('[data-md-sticky-add]');

  [buyNow, addBtn, stickyBtn].forEach((el) => {
    if (!el) return;
    delete el.dataset.mdSubmitting;
    el.removeAttribute('disabled');
  });

  const variant = this.getPickerVariant();
  if (variant) {
    this.updateAvailability(variant);
    return;
  }

  const input = this.root.querySelector('[data-md-product-form] [name="id"]');
  if (input && input.value && buyNow) buyNow.removeAttribute('disabled');
  if (input && input.value && addBtn) addBtn.removeAttribute('disabled');
};

MercadaoProductPage.prototype.bindAddCartBusy = function () {
  const self = this;
  const form = this.root.querySelector('[data-md-product-form]');
  if (!form) return;

  const onDone = () => {
    self.setCommerceBusy(false);
    form.querySelectorAll('[data-md-add-cart], [data-md-buy-now]').forEach((el) => {
      delete el.dataset.mdSubmitting;
    });
  };

  form.addEventListener(
    'submit',
    () => {
      self.setCommerceBusy(true);
      form.querySelectorAll('[data-md-add-cart]').forEach((el) => {
        el.dataset.mdSubmitting = 'true';
      });
    },
    true
  );

  document.addEventListener('cart:update', onDone);
  document.addEventListener('cart:error', onDone);
};

MercadaoProductPage.prototype.bindBuyNow = function () {
  const self = this;
  const btn = this.root.querySelector('[data-md-buy-now]');
  const form = this.root.querySelector('[data-md-product-form]');
  if (!btn || !form) return;

  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    if (btn.disabled || btn.dataset.mdSubmitting === 'true' || btn.classList.contains('md-pdp__btn--busy')) {
      return;
    }
    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput || !variantInput.value) return;

    btn.dataset.mdSubmitting = 'true';
    self.setCommerceBusy(true);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantInput.value), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('cart add failed');
      self.setCommerceBusy(false);
      delete btn.dataset.mdSubmitting;
      btn.removeAttribute('disabled');
      window.location.assign('/checkout');
    } catch (err) {
      self.resetCommerceState();
      const variant = self.getPickerVariant();
      if (variant && variant.available === false) {
        btn.disabled = true;
      }
    }
  });
};

MercadaoProductPage.prototype.bindNavScroll = function () {
  const links = this.root.querySelectorAll('[data-md-anchor]');
  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || href.indexOf('#') !== 0) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      links.forEach((l) => l.classList.remove('is-active'));
      link.classList.add('is-active');
    });
  });
};

MercadaoProductPage.prototype.bindVariantUpdates = function () {
  const self = this;
  const productId = String(this.root.dataset.productId || '');

  const handler = (event) => {
    const variant = event.detail && event.detail.resource;
    const eventProductId = event.detail && event.detail.data && event.detail.data.productId;

    if (eventProductId && String(eventProductId) !== productId) return;

    if (variant && variant.price != null) {
      self.updatePrices(variant);
    }
    if (variant && variant.id) {
      self.updateAvailability(variant);
    }
  };

  const section = this.root.closest('.shopify-section');
  if (section) section.addEventListener(ThemeEvents.variantUpdate, handler);
  document.addEventListener(ThemeEvents.variantUpdate, handler);
};

MercadaoProductPage.prototype.updatePrices = function (variant) {
  const price = variant.price;
  const compare =
    variant.compare_at_price && variant.compare_at_price > price ? variant.compare_at_price : null;
  const pix = Math.round(price * (1 - this.pixDiscount));
  const installment = Math.round(price / this.installments);
  const savings = compare ? compare - price : 0;
  const onSale = Boolean(compare && compare > price);

  const compareEl = this.root.querySelector('[data-md-compare]');
  const pixEl = this.root.querySelector('[data-md-pix]');
  const instEl = this.root.querySelector('[data-md-installments]');
  const saveEl = this.root.querySelector('[data-md-savings]');
  const badgeEl = this.root.querySelector('[data-md-discount-badge]');
  const dropWrap = this.root.querySelector('.md-pdp__offer-tags');
  const dropEl = this.root.querySelector('[data-md-price-drop]');
  const shippingTag = this.root.querySelector('[data-md-shipping-tag]');
  const promoOff = this.root.querySelector('[data-md-promo-off]');
  const promoBar = this.root.querySelector('[data-md-promo-bar]');

  if (pixEl) {
    const tpl = pixEl.dataset.template || '';
    pixEl.textContent = tpl.replace('__PRICE__', formatMoney(pix, this.moneyFormat, this.currency));
  }
  if (instEl) {
    const tpl = instEl.dataset.template || '';
    instEl.textContent = tpl
      .replace('__COUNT__', String(this.installments))
      .replace('__PRICE__', formatMoney(installment, this.moneyFormat, this.currency));
  }

  if (onSale) {
    if (compareEl) {
      compareEl.classList.remove('hidden');
      compareEl.textContent = formatMoney(compare, this.moneyFormat, this.currency);
    }
    if (saveEl) {
      saveEl.classList.remove('hidden');
      const saveValue = saveEl.querySelector('[data-md-savings-value]');
      if (saveValue) saveValue.textContent = formatMoney(savings, this.moneyFormat, this.currency);
    }
    if (dropEl) dropEl.classList.remove('hidden');
    if (dropWrap) dropWrap.classList.remove('hidden');
    const pct = Math.round((savings * 100) / compare);
    if (badgeEl) {
      const tpl = badgeEl.dataset.template || '__PCT__% OFF';
      badgeEl.textContent = tpl.replace('__PCT__', String(pct)).replace('{{ percent }}', String(pct));
      badgeEl.classList.remove('hidden');
    }
    if (promoOff) promoOff.textContent = pct + '% OFF';
    if (promoBar) promoBar.classList.remove('hidden');
  } else {
    if (compareEl) compareEl.classList.add('hidden');
    if (saveEl) saveEl.classList.add('hidden');
    if (dropEl) dropEl.classList.add('hidden');
    if (dropWrap) {
      if (shippingTag && !shippingTag.classList.contains('hidden')) {
        dropWrap.classList.remove('hidden');
      } else {
        dropWrap.classList.add('hidden');
      }
    }
    if (badgeEl) badgeEl.classList.add('hidden');
    if (promoBar) promoBar.classList.add('hidden');
  }

  this.updateShippingTag(price);

  const cashbackEl = this.root.querySelector('[data-md-cashback]');
  if (cashbackEl && this.cashbackPercent > 0) {
    const cb = Math.round(price * this.cashbackPercent);
    const tpl = cashbackEl.dataset.template || '';
    cashbackEl.textContent = tpl.replace('__AMOUNT__', formatMoney(cb, this.moneyFormat, this.currency));
  }
};

MercadaoProductPage.prototype.updateShippingTag = function (priceCents) {
  const tag = this.root.querySelector('[data-md-shipping-tag]');
  const text = tag ? tag.querySelector('[data-md-shipping-text]') : null;
  if (!tag || !text) return;

  const pix = Math.round(priceCents * (1 - this.pixDiscount));
  const qualifies = pix >= this.freeShippingMin;

  text.textContent = qualifies
    ? this.tplFreeShipping
    : this.tplFreeShippingFrom.replace('__AMOUNT__', formatMoney(this.freeShippingMin, this.moneyFormat, this.currency));
};

MercadaoProductPage.prototype.updateAvailability = function (variant) {
  const meta = this.root.querySelector('[data-md-stock]');
  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const addBtn = this.root.querySelector('[data-md-add-cart]');
  const notify = this.root.querySelector('[data-md-notify]');
  const available = Boolean(variant.available);
  const soldOutLabel = this.root.dataset.labelSoldOut || 'Esgotado';
  const buyNowLabel = this.root.dataset.labelBuyNow || 'Comprar agora';
  const addCartLabel = this.root.dataset.labelAddCart || 'Adicionar ao carrinho';

  if (meta) {
    meta.textContent = available ? meta.dataset.inStock || '' : meta.dataset.outStock || '';
    meta.classList.toggle('md-pdp__meta--in-stock', available);
  }
  if (buyNow) {
    buyNow.disabled = !available;
    buyNow.textContent = available ? buyNowLabel : soldOutLabel;
  }
  if (addBtn) {
    addBtn.disabled = !available;
    addBtn.textContent = available ? addCartLabel : soldOutLabel;
  }
  if (notify) notify.classList.toggle('hidden', available);

  const sticky = document.querySelector('mercadao-sticky-cart');
  if (sticky) {
    const stickyBtn = sticky.querySelector('[data-md-sticky-add]');
    const stickySoldOut = sticky.dataset.labelSoldOut || soldOutLabel;
    const stickyAdd = sticky.dataset.labelAdd || addCartLabel;
    if (stickyBtn) {
      stickyBtn.disabled = !available;
      stickyBtn.textContent = available ? stickyAdd : stickySoldOut;
    }
    sticky.dataset.variantAvailable = available ? 'true' : 'false';
  }
};

MercadaoProductPage.prototype.parsePromoEnd = function (raw) {
  const target = (raw || '').trim();
  if (!target) {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime();
  }
  const normalized = target.indexOf('T') >= 0 ? target : target + 'T23:59:59';
  let end = Date.parse(normalized.replace(' ', 'T'));
  if (Number.isNaN(end)) end = Date.parse(target);
  if (Number.isNaN(end)) {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime();
  }
  return end;
};

MercadaoProductPage.prototype.initCountdown = function () {
  const wrap = this.root.querySelector('[data-md-countdown]');
  if (!wrap) return;

  const els = {
    d: wrap.querySelector('[data-cd-days]'),
    h: wrap.querySelector('[data-cd-hours]'),
    m: wrap.querySelector('[data-cd-minutes]'),
    s: wrap.querySelector('[data-cd-seconds]'),
  };

  const end = this.parsePromoEnd(wrap.dataset.target || this.promoEnd);
  const pad = (n) => String(n).padStart(2, '0');

  const tick = () => {
    const diff = Math.max(0, end - Date.now());
    if (diff <= 0) {
      if (els.d) els.d.textContent = '00';
      if (els.h) els.h.textContent = '00';
      if (els.m) els.m.textContent = '00';
      if (els.s) els.s.textContent = '00';
      return;
    }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff / 3600000) % 24);
    const m = Math.floor((diff / 60000) % 60);
    const s = Math.floor((diff / 1000) % 60);
    if (els.d) els.d.textContent = pad(d);
    if (els.h) els.h.textContent = pad(h);
    if (els.m) els.m.textContent = pad(m);
    if (els.s) els.s.textContent = pad(s);

    const daysWrap = wrap.querySelector('[data-cd-days-wrap]');
    const daysSep = wrap.querySelector('.countdown-bar__sep--days');
    const showDays = d > 0;
    if (daysWrap) daysWrap.classList.toggle('hidden', !showDays);
    if (daysSep) daysSep.classList.toggle('hidden', !showDays);
  };

  tick();
  window.setInterval(tick, 1000);
};

MercadaoProductPage.prototype.initViewers = function () {
  const wrap = this.root.querySelector('[data-md-viewers]');
  const text = wrap ? wrap.querySelector('[data-md-viewers-text]') : null;
  if (!text) return;

  const pick = () => {
    const min = Math.min(this.viewersMin, this.viewersMax);
    const max = Math.max(this.viewersMin, this.viewersMax);
    const count = Math.floor(Math.random() * (max - min + 1)) + min;
    text.textContent = this.tplViewers.replace('__COUNT__', String(count));
  };

  if (!text.textContent.trim()) pick();
  window.setInterval(pick, 12000 + Math.random() * 8000);
};

function initMercadaoProductPage() {
  const hash = (window.location.hash || '').replace('#', '');
  const hasContentAnchor =
    hash === 'md-pdp-ficha' || hash === 'md-pdp-descricao' || hash === 'md-pdp-detalhes';
  if (hasContentAnchor) {
    const target = document.getElementById(hash);
    if (target instanceof HTMLDetailsElement) {
      target.open = true;
    }
    requestAnimationFrame(() => {
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  } else {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }

  document.querySelectorAll('[data-md-pdp]').forEach((root) => {
    if (root instanceof HTMLElement) {
      new MercadaoProductPage(root);
    }
  });

  if (window.MercadaoPdpCommerce && typeof window.MercadaoPdpCommerce.hardReset === 'function') {
    window.MercadaoPdpCommerce.hardReset();
  }
}

window.MercadaoPdpCommerce = window.MercadaoPdpCommerce || {};
window.MercadaoPdpCommerce.resetInstances = function () {
  document.querySelectorAll('[data-md-pdp]').forEach((root) => {
    if (root._mercadaoPdp && typeof root._mercadaoPdp.resetCommerceState === 'function') {
      root._mercadaoPdp.resetCommerceState();
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMercadaoProductPage);
} else {
  initMercadaoProductPage();
}
